import { useEffect, useRef } from 'react';
import { UseFormSetValue } from 'react-hook-form';

import { ProductForm } from '@app/schemas/catalog';
import { Organization } from '@app/types/organization';
import { convertNewLine } from '@app/utils';
import {
  isFullWorkLocationDetail,
  isManualWorkLocationInput,
  isManualWorkplaceInput,
} from '@app/utils/productOrganization';

/** 組織から決まる求人フォームの勤務先・勤務地の初期値 */
export type ProductWorkLocationValues = Pick<
  ProductForm,
  | 'access'
  | 'isOrgAgency'
  | 'workAddress1'
  | 'workAddress2'
  | 'workCity'
  | 'workLocationOther'
  | 'workPostalCode'
  | 'workPrefecture'
>;

/** 勤務地情報の保存に必要なフォームの入力値 */
type WorkLocationFormInput = Pick<
  ProductForm,
  | 'access'
  | 'workAddress1'
  | 'workAddress2'
  | 'workCity'
  | 'workLocationOther'
  | 'workPostalCode'
  | 'workPrefecture'
>;

/** 勤務地(都道府県・市区町村など)を組織の住所から引き継がず、手入力値を使うかどうか */
function shouldUseManualWorkLocation(
  organization: Partial<Organization>,
  workLocationOther: boolean
): boolean {
  return isManualWorkLocationInput(organization) || workLocationOther;
}

/**
 * 求人保存時の勤務地情報(customFields)を組み立てる。
 * NOTE:人材紹介企業は勤務地の詳細を掲載しないため町名・番地/建物名/アクセス情報は空で保存する。
 * 郵便番号は区分に関わらず入力値をそのまま保存する
 */
export function toWorkLocationCustomFields(
  formValue: WorkLocationFormInput,
  organization: Partial<Organization>
) {
  const shouldShowFullWorkLocation = isFullWorkLocationDetail(organization);
  const shouldUseManualWorkLocationValue = shouldUseManualWorkLocation(
    organization,
    formValue.workLocationOther
  );
  const getDetailValue = (manualValue: string, organizationValue?: string) => {
    if (!shouldShowFullWorkLocation) {
      return '';
    }
    return shouldUseManualWorkLocationValue
      ? manualValue
      : organizationValue ?? '';
  };
  return {
    access: getDetailValue(formValue.access, organization.customFields?.access),
    workAddress1: getDetailValue(
      formValue.workAddress1,
      organization.addressLine3
    ),
    workAddress2: getDetailValue(
      formValue.workAddress2,
      organization.customFields?.addressLine4
    ),
    workPostalCode: shouldUseManualWorkLocationValue
      ? formValue.workPostalCode
      : organization.postalCode ?? '',
  };
}

/** 求人保存時のlocationIds(都道府県・市区町村)を組み立てる */
export function toWorkLocationIds(
  formValue: WorkLocationFormInput,
  organization: Partial<Organization>
): string[] {
  const shouldUseManualWorkLocationValue = shouldUseManualWorkLocation(
    organization,
    formValue.workLocationOther
  );
  return [
    shouldUseManualWorkLocationValue
      ? formValue.workPrefecture
      : organization.addressLine1 ?? '',
    shouldUseManualWorkLocationValue
      ? formValue.workCity
      : organization.addressLine2 ?? '',
  ];
}

/** 求人データから復元した勤務地情報 */
export type ProductWorkLocationFields = {
  access: string;
  /** 組織の住所と異なる(=勤務地情報が手入力)かどうか */
  isWorkLocationOther: boolean;
  workAddress1: string;
  workAddress2: string;
  workCity: string;
  workPostalCode: string;
  workPrefecture: string;
};

/** extractProductWorkLocationが必要とする求人データの形 */
type ProductWorkLocationSource = {
  customFields?: {
    access?: string;
    workAddress1?: string;
    workAddress2?: string;
    workPostalCode?: string;
  };
  locations?: { id: string; type: string }[];
};

/**
 * 求人データから勤務地情報を取り出し、組織の住所と異なるかを判定する。
 * NOTE:編集・複製・一括編集でフォームへ復元する際に使う
 */
export function extractProductWorkLocation(
  product: ProductWorkLocationSource,
  organization: Partial<Organization>
): ProductWorkLocationFields {
  const workPostalCode = product.customFields?.workPostalCode ?? '';
  const workCity = product.locations?.find((l) => l.type === 'city')?.id ?? '';
  const workPrefecture =
    product.locations?.find((l) => l.type === 'prefecture')?.id ?? '';
  const workAddress1 = product.customFields?.workAddress1 ?? '';
  const workAddress2 = product.customFields?.workAddress2 ?? '';
  const access = convertNewLine(product.customFields?.access ?? '');
  return {
    access,
    isWorkLocationOther:
      organization.postalCode !== workPostalCode ||
      organization.addressLine1 !== workPrefecture ||
      organization.addressLine2 !== workCity ||
      organization.addressLine3 !== workAddress1 ||
      (organization.customFields?.addressLine4 ?? '') !== workAddress2 ||
      convertNewLine(organization.customFields?.access ?? '') !== access,
    workAddress1,
    workAddress2,
    workCity,
    workPostalCode,
    workPrefecture,
  };
}

/** 求人データを作成・一括編集フォームの勤務地項目へ復元する */
export function getRestoredProductWorkLocationValues(
  product: ProductWorkLocationSource,
  organization: Partial<Organization>,
  /** 一括編集で対象の求人間で値が異なる項目かどうか。trueの場合は空欄に復元する */
  { isMixed = false }: { isMixed?: boolean } = {}
): ProductWorkLocationValues {
  const extracted = extractProductWorkLocation(product, organization);
  const value = <T>(fieldValue: T, emptyValue: T) =>
    isMixed ? emptyValue : fieldValue;
  return {
    access: value(extracted.access, ''),
    isOrgAgency: isManualWorkplaceInput(organization),
    workAddress1: value(extracted.workAddress1, ''),
    workAddress2: value(extracted.workAddress2, ''),
    workCity: value(extracted.workCity, ''),
    workLocationOther:
      isManualWorkLocationInput(organization) ||
      isMixed ||
      extracted.isWorkLocationOther,
    workPostalCode: value(extracted.workPostalCode, ''),
    workPrefecture: value(extracted.workPrefecture, ''),
  };
}

/**
 * 組織から求人フォームの勤務先・勤務地の初期値を組み立てる。
 * NOTE:人材紹介企業は法人住所が勤務地ではないため、初期表示せず必ず手入力させる
 */
export function getProductWorkLocationValues(
  organization: Partial<Organization>
): ProductWorkLocationValues {
  if (isManualWorkLocationInput(organization)) {
    return {
      access: '',
      isOrgAgency: true,
      workAddress1: '',
      workAddress2: '',
      workCity: '',
      workLocationOther: true,
      workPostalCode: '',
      workPrefecture: '',
    };
  }
  return {
    access: convertNewLine(organization.customFields?.access ?? ''),
    isOrgAgency: false,
    workAddress1: organization.addressLine3 ?? '',
    workAddress2: organization.customFields?.addressLine4 ?? '',
    workCity: organization.addressLine2 ?? '',
    workLocationOther: false,
    workPostalCode: organization.postalCode ?? '',
    workPrefecture: organization.addressLine1 ?? '',
  };
}

/**
 * 組織情報はRecoilの初期値(空の組織)から後で実データに差し替わるため、useFormのdefaultValuesには反映されない。
 * 新規登録時は組織が確定した時点(組織切替時も含む)で勤務先・勤務地の初期値を同期する
 */
export function useSyncProductWorkLocation({
  isSkipped,
  organization,
  setValue,
}: {
  /** 編集・複製時は求人データ読み込み時のresetで値を設定するため対象外にする */
  isSkipped: boolean;
  organization: Organization;
  setValue: UseFormSetValue<ProductForm>;
}): void {
  const syncedOrganizationIdRef = useRef('');
  useEffect(() => {
    if (isSkipped) {
      return;
    }
    // NOTE:同じ組織で再同期すると入力済みの勤務地情報を上書きしてしまうため、組織が変わった場合のみ実行する
    if (
      !organization.id ||
      syncedOrganizationIdRef.current === organization.id
    ) {
      return;
    }
    syncedOrganizationIdRef.current = organization.id;
    const values = getProductWorkLocationValues(organization);
    setValue('isOrgAgency', values.isOrgAgency);
    setValue('workLocationOther', values.workLocationOther);
    setValue('access', values.access);
    setValue('workAddress1', values.workAddress1);
    setValue('workAddress2', values.workAddress2);
    setValue('workCity', values.workCity);
    setValue('workPostalCode', values.workPostalCode);
    setValue('workPrefecture', values.workPrefecture);
  }, [isSkipped, organization, setValue]);
}
